import React, { useEffect, useCallback } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Button, Box, Text, Switch, Select } from '@rsos/base-components';
import InputText from '@rsos/components/capstone/base/form/inputs/InputText';
import type {
  ConfigureSSOProps,
  ConfigureSSOInitialValues,
  SSOConfigurationValues,
} from '../../types/sso';

export const BUTTON_TEXT = 'Save Configuration Changes';

const baseValidationSchema = {
  ssoEnabled: Yup.boolean().required(),
  jitEnabled: Yup.boolean().required(),
  selectedRole: Yup.string().when('ssoEnabled', {
    is: true,
    then: schema =>
      schema.required('Default role is required when SSO is enabled'),
    otherwise: schema => schema.nullable(),
  }),
};

const extendedValidationSchema = {
  ...baseValidationSchema,
  ssoGroupAttribute: Yup.string()
    .max(40, 'Group Attribute cannot exceed 40 characters')
    .test(
      'no-leading-trailing-spaces',
      'Group Attribute cannot have leading or trailing spaces',
      value => {
        if (!value) return true; // empty values allowed
        return value === value.trim();
      },
    ),
};

const ConfigureSSO: React.FC<ConfigureSSOProps> = ({
  initialValues = {
    selectedRole: '',
    ssoEnabled: false,
    ssoGroupAttribute: '',
    jitEnabled: false,
  },
  roles = [],
  onSubmit = (values, done) => {
    // eslint-disable-next-line no-console
    console.error(
      'ConfigureSSO: onSubmit callback not provided. Form submission will not work.',
    );
    done();
  },
}) => {
  const formik = useFormik<ConfigureSSOInitialValues>({
    initialValues: {
      ssoEnabled: initialValues.ssoEnabled || false,
      jitEnabled: initialValues.jitEnabled || false,
      selectedRole: initialValues.selectedRole || '',
      ssoGroupAttribute: initialValues.ssoGroupAttribute || '',
    },
    validationSchema: Yup.object(extendedValidationSchema),
    enableReinitialize: true,
    onSubmit: (values, { setSubmitting }) => {
      const submitData: SSOConfigurationValues = {
        ssoEnabled: values.ssoEnabled,
        jitEnabled: values.jitEnabled,
        selectedRole: values.selectedRole,
        ssoGroupAttribute: values.ssoGroupAttribute,
      };

      onSubmit(submitData, () => {
        setSubmitting(false);
      });
    },
  });

  useEffect(() => {
    if (roles.length > 0 && initialValues.selectedRole) {
      const selectedRole = roles.find(
        role => role.value === initialValues.selectedRole,
      );
      if (selectedRole) {
        formik.setFieldValue('selectedRole', selectedRole.value);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roles, initialValues.selectedRole]);

  const selectedRoleObject = roles.find(
    role => role.value === formik.values.selectedRole,
  );

  const handleSwitchChange = useCallback(() => {
    formik.setFieldValue('ssoEnabled', !formik.values.ssoEnabled);
  }, [formik]);

  const handleJITSwitchChange = useCallback(() => {
    formik.setFieldValue('jitEnabled', !formik.values.jitEnabled);
  }, [formik]);

  const handleRoleChange = useCallback(
    (selected: { value: string } | null) => {
      formik.setFieldValue('selectedRole', selected?.value || '');
    },
    [formik],
  );

  return (
    <form onSubmit={formik.handleSubmit}>
      <Text
        size="header"
        fontWeight="semibold"
        color="primaryText"
        mt={20}
        mb={32}
      >
        Configure SSO
      </Text>

      <Box>
        <Box mb={32}>
          <Box mb={12}>
            <Text size="normal" color="primaryText" fontWeight="semibold">
              Enable SSO
            </Text>
          </Box>
          <Box horizontal align="center" mb={12}>
            <Switch
              isOn={formik.values.ssoEnabled}
              onClick={handleSwitchChange}
              onOffLabel
            />
          </Box>
          <Box>
            <Text size="small" color="secondaryText">
              Select this option to enable SSO for your organization and disable
              passwords for roles without "Manage SSO" access. Users with
              'Manage SSO' access retain the ability to bypass SSO to prevent
              lockouts.
            </Text>
          </Box>
        </Box>

        <Box mb={32}>
          <Box mb={12}>
            <Text size="normal" color="primaryText" fontWeight="semibold">
              Enable JIT (Just-in-Time) Provisioning
            </Text>
          </Box>
          <Box horizontal align="center" mb={12}>
            <Switch
              isOn={formik.values.jitEnabled || false}
              onClick={handleJITSwitchChange}
              onOffLabel
            />
          </Box>
          <Box>
            <Text size="small" color="secondaryText">
              Enable automatic user provisioning for new users who authenticate
              via SSO. When enabled, new users will be automatically created
              with the default role when they first sign in through SSO. Leave
              this field EMPTY unless your domain is shared across multiple
              RapidSOS Jurisdiction accounts.
            </Text>
          </Box>
        </Box>

        {formik.values.ssoEnabled && (
          <>
            <Box mb={32}>
              <Box mb={12}>
                <Text size="normal" color="primaryText" fontWeight="semibold">
                  Group Attribute (optional)
                </Text>
              </Box>
              <Box mb={12}>
                <Box width="100%" maxWidth={300}>
                  <InputText
                    id="ssoGroupAttribute"
                    name="ssoGroupAttribute"
                    placeholder="Group ID"
                    value={formik.values.ssoGroupAttribute || ''}
                    shouldDisplayClearButton={false}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    setFieldValue={formik.setFieldValue}
                    error={
                      formik.touched.ssoGroupAttribute &&
                      formik.errors.ssoGroupAttribute
                        ? formik.errors.ssoGroupAttribute
                        : null
                    }
                    shouldErrorTakeSpace={true}
                  />
                </Box>
              </Box>
              <Box>
                <Text size="small" color="secondaryText">
                  Specify the SAML attribute name that contains group membership
                  information. This attribute is used for Just-in-Time (JIT)
                  provisioning to automatically assign users to appropriate
                  groups when they first log in via SSO.
                </Text>
              </Box>
            </Box>

            <Box mb={32}>
              <Box mb={12}>
                <Text size="normal" color="primaryText" fontWeight="semibold">
                  Default Role for New SSO Users
                </Text>
              </Box>
              <Box mb={12}>
                <Box width="100%" maxWidth={300}>
                  <Select
                    name="selectedRole"
                    options={roles}
                    placeholder={
                      selectedRoleObject?.label || 'Select a role...'
                    }
                    isClearable={false}
                    isSearchable={false}
                    onChange={handleRoleChange}
                    value={selectedRoleObject}
                  />
                </Box>
                {formik.touched.selectedRole && formik.errors.selectedRole && (
                  <Box mt={8}>
                    <Text size="small" color="lightDanger">
                      {formik.errors.selectedRole}
                    </Text>
                  </Box>
                )}
              </Box>
              <Box>
                <Text size="small" color="secondaryText">
                  Users joining the organization through Single Sign-On will be
                  assigned this default role through Just-in-Time (JIT)
                  provisioning.
                </Text>
              </Box>
            </Box>
          </>
        )}

        <Box mt={40}>
          <Button
            type="submit"
            btnType="primary"
            isLoadingProp={formik.isSubmitting}
            disabled={!formik.dirty || !formik.isValid || formik.isSubmitting}
          >
            {formik.isSubmitting ? 'Saving...' : BUTTON_TEXT}
          </Button>
        </Box>
      </Box>
    </form>
  );
};

export default ConfigureSSO;
